import React from 'react';
import { AlertCircle } from 'lucide-react';

/**
 * ValidatedInput - Reusable input component with validation error display
 * 
 * Props:
 * - name: Field name for react-hook-form
 * - label: Label text for the input
 * - type: Input type (text, email, number, date, etc.)
 * - register: react-hook-form register function
 * - errors: react-hook-form errors object
 * - placeholder: Placeholder text
 * - icon: Optional icon component
 * - className: Additional CSS classes
 * - required: Show required indicator
 * - disabled: Disable the input
 * - autoComplete: Autocomplete attribute
 * - ...rest: Other input props
 */
const ValidatedInput = ({
  name,
  label,
  type = 'text',
  register,
  errors,
  placeholder,
  icon: Icon,
  className = '',
  required = false,
  disabled = false,
  autoComplete,
  ...rest
}) => {
  const hasError = errors && errors[name];
  const errorMessage = hasError ? errors[name].message : null;

  return (
    <div className={`validated-input-wrapper ${className}`}>
      {label && (
        <label 
          htmlFor={name} 
          className="validated-input-label"
        >
          {label}
          {required && <span className="required-indicator"> *</span>}
        </label>
      )}
      
      <div className="validated-input-container">
        {Icon && (
          <div className="input-icon-wrapper">
            <Icon className="input-icon" />
          </div>
        )}
        
        <input
          id={name}
          type={type}
          {...register(name)}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete={autoComplete}
          className={`validated-input ${hasError ? 'input-error' : ''} ${Icon ? 'input-with-icon' : ''}`}
          aria-invalid={hasError ? 'true' : 'false'}
          aria-describedby={hasError ? `${name}-error` : undefined}
          {...rest}
        />
      </div>

      {hasError && (
        <div 
          id={`${name}-error`}
          className="validation-error"
          role="alert"
        >
          <AlertCircle className="error-icon" />
          <span className="error-message">{errorMessage}</span>
        </div>
      )}
    </div>
  );
};

export default ValidatedInput;

